import { inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { ApiErrorCodes, ApiErrorStatusResponse, TokenExpireTimestampDto } from '@workspace/data-types';
import { ApiService } from '@workspace/ng-http';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AuthStore } from '../ngrx';

@Service()
export class UserRegistrationAuthService {
  private refreshTimer: number | null = null;
  private debugMode = true;
  private readonly REFRESH_BUFFER_MS = 10000;

  private api = inject(ApiService);
  private router = inject(Router);
  private authStore = inject(AuthStore);

  clearTokenRefreshSchedule(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
      if (this.debugMode) {
        console.debug('[AuthService] Token refresh schedule cleared.');
      }
    }
  }

  executeTokenRefresh(): Observable<void> {
    return this.api
      .auth()
      .userRegistration()
      .refreshToken()
      .pipe(
        tap((expiryData: TokenExpireTimestampDto) => {
          if (this.debugMode) {
            console.debug('[AuthService] Token refreshed successfully. Scheduling next refresh...');
          }
          this.scheduleTokenRefresh(expiryData);
        }),
        switchMap(() => of(void 0)),
        catchError((errorResponse) => {
          const apiError = errorResponse.error as ApiErrorStatusResponse;
          if (apiError && this.debugMode) {
            if (apiError.code === ApiErrorCodes.BAD_COOKIES) {
              console.error('[AuthService] Refresh Token failed or expired.', apiError);
            } else if (apiError.code === ApiErrorCodes.OBJECT_NOT_FOUND) {
              console.error('[AuthService] Object not found.', apiError);
            } else if (apiError.code === ApiErrorCodes.BAD_QUERY) {
              console.error('[AuthService] Bad query.', apiError);
            }
          }

          return throwError(() => errorResponse);
        }),
      );
  }

  logOut() {
    this.clearTokenRefreshSchedule();
    this.authStore.logout();
    void this.router.navigate(['/auth/sign-in']);
  }

  // Method called after login/successful refresh to set the timer
  // Inside your AuthService in Angular (after successful login/refresh)
  scheduleTokenRefresh(expiryData: TokenExpireTimestampDto): void {
    // 1. Clear existing refresh schedule
    this.clearTokenRefreshSchedule();

    const { userRegistrationAccessTokenInMs, userRegistrationRefreshTokenInMs } = expiryData;

    // 2. Check if the Refresh Token is still valid. If not, scheduling is impossible.
    if (userRegistrationRefreshTokenInMs <= 0) {
      if (this.debugMode) {
        console.warn(
          '[AuthService] Refresh token is expired. Cannot schedule proactive refresh. Logging out.',
        );
      }
      return this.logOut();
    }

    // 3. Calculate the actual time to wait before starting the refresh
    const timeUntilProactiveRefresh = userRegistrationAccessTokenInMs - this.REFRESH_BUFFER_MS;

    // 4. Handle immediate refresh necessity (e.g., token expired while tab was backgrounded)
    if (timeUntilProactiveRefresh <= 0) {
      if (userRegistrationRefreshTokenInMs - this.REFRESH_BUFFER_MS <= 0) {
        if (this.debugMode) {
          console.warn(
            '[AuthService] Refresh token is expired. Cannot schedule proactive refresh. Logging out.',
          );
        }
        return this.logOut();
      } else {
        if (this.debugMode) {
          console.debug(
            '[AuthService] Access Token is already expired or near expiry. Executing refresh immediately.',
          );
        }
        // Execute refresh, which will then schedule the next one automatically on success
        this.executeTokenRefresh().subscribe();
        return;
      }
    }

    // 5. Set the new timer
    if (this.debugMode) {
      console.debug(
        `[AuthService] Scheduling proactive refresh in ${timeUntilProactiveRefresh / 1000} seconds.`,
      );
    }
    this.refreshTimer = window.setTimeout(() => {
      if (this.debugMode) {
        console.debug('[AuthService] Proactive refresh timer triggered. Executing refresh...');
      }
      // The successful completion of executeTokenRefresh() will recursively call
      // scheduleTokenRefresh() with the *new* expiry data.
      this.executeTokenRefresh().subscribe();
    }, timeUntilProactiveRefresh);
  }
}
